import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
  Dispatch,
  SetStateAction,
} from "react";
import { WebAuth } from "auth0-js";
import jwt_decode from "jwt-decode";
import environment from "~libs/environment";
import { authStateSync } from "~components/auth/actions";
import { useDispatch } from "react-redux";
import localRepository from "../../localRepository";
import getMGUser from "~libs/getMGUser";

interface AuthState {
  accessToken: string | null;
  idToken: string | null;
  expiresAt: number;
  user: any;
}

// Hidrata authState desde el JWT almacenado en localRepository en lugar de
// arrancar con expiresAt=0 y depender de un renewSession() inicial. El
// renewSession via auth0.checkSession falla en localhost por cookies
// 3rd-party y cascadea a logout(), expulsando al usuario de rutas publicas
// como /denuncia-tu-siniestro (mismo patron de SEG-1437) y dejandolo sin
// sesion para volver a las privadas. Decodificando el access token tenemos
// expiresAt y user reales sin llamar al servidor; useRefreshToken sigue
// programando la renovacion proactiva 2 min antes del vencimiento.
const getInitialAuthState = (): AuthState => {
  const accessToken = localRepository.accessToken.get();

  if (!accessToken) {
    return { accessToken: null, idToken: null, expiresAt: 0, user: null };
  }

  try {
    const decoded: any = jwt_decode(accessToken);
    const expiresAt = decoded.exp ? decoded.exp * 1000 : 0;

    if (expiresAt <= new Date().getTime()) {
      return { accessToken: null, idToken: null, expiresAt: 0, user: null };
    }

    return {
      accessToken,
      idToken: null,
      expiresAt,
      user: getMGUser(decoded),
    };
  } catch {
    return { accessToken: null, idToken: null, expiresAt: 0, user: null };
  }
};

interface AuthContextValue {
  auth0: WebAuth;
  authState: AuthState;
  updateAuthState: Dispatch<SetStateAction<AuthState>>;
}

const generateAuth = (): WebAuth =>
  new WebAuth({
    domain: environment.auth0.domain,
    clientID: environment.auth0.clientId,
    redirectUri: `${environment.myUrl}/callback`,
    responseType: "token id_token",
    scope: "openid profile email offline_access",
    audience: `http://mg-group.com.ar/apis`,
  });

const Auth0Context = createContext<AuthContextValue | null>(null);

const useAuthState = (): [AuthState, Dispatch<SetStateAction<AuthState>>] => {
  const [authState, setAuthState] = useState<AuthState>(getInitialAuthState);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(authStateSync(authState));
  }, [dispatch, authState]);

  return [authState, setAuthState];
};

const useContextValue = (): AuthContextValue => {
  const [authState, updateAuthState] = useAuthState();
  return {
    auth0: generateAuth(),
    authState,
    updateAuthState,
  };
};

interface Auth0ProviderProps {
  children: ReactNode;
}

export const Auth0Provider: React.FC<Auth0ProviderProps> = ({ children }) => {
  const value = useContextValue();
  return (
    <Auth0Context.Provider value={value}>{children} </Auth0Context.Provider>
  );
};

export const useAuth0Context = (): AuthContextValue | null => {
  return useContext(Auth0Context);
};
